import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import config from '../config/config';
import './UserManagement.css';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [userToChangePassword, setUserToChangePassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const { user } = useAuth();

  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    fullName: '',
    email: '',
    role: 'contract_user'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL}/api/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const usersData = await response.json();
        setUsers(usersData);
      } else {
        setErrorMessageWithTimeout('Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setErrorMessageWithTimeout('Error fetching users');
    } finally {
      setLoading(false);
    }
  };

  const setSuccessMessageWithTimeout = (message) => {
    setSuccessMessage(message);
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };

  const setErrorMessageWithTimeout = (message) => {
    setError(message);
    setTimeout(() => {
      setError('');
    }, 5000);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL}/api/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newUser)
      });

      if (response.ok) {
        await fetchUsers();
        setShowUserForm(false);
        setNewUser({
          username: '',
          password: '',
          fullName: '',
          email: '',
          role: 'contract_user'
        });
        setError('');
        setSuccessMessageWithTimeout('User created successfully!');
      } else {
        const errorData = await response.json();
        setErrorMessageWithTimeout(errorData.error || 'Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      setErrorMessageWithTimeout('Error creating user');
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL}/api/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        await fetchUsers();
        setEditingUser(null);
        setError('');
        setSuccessMessageWithTimeout('User updated successfully!');
      } else {
        const errorData = await response.json();
        setErrorMessageWithTimeout(errorData.error || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      setErrorMessageWithTimeout('Error updating user');
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL}/api/users/${userToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        await fetchUsers();
        setShowDeleteModal(false);
        setUserToDelete(null);
        setError('');
        setSuccessMessageWithTimeout('User deleted successfully!');
      } else {
        const errorData = await response.json();
        setErrorMessageWithTimeout(errorData.error || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      setErrorMessageWithTimeout('Error deleting user');
    }
  };

  const handleResetPassword = async (userId, newPassword) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL}/api/users/${userId}/admin-reset-password`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ newPassword })
      });

      if (response.ok) {
        setError('');
        setShowPasswordModal(false);
        setUserToChangePassword(null);
        setNewPassword('');
        setSuccessMessageWithTimeout('Password reset successfully!');
      } else {
        const errorData = await response.json();
        setErrorMessageWithTimeout(errorData.error || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      setErrorMessageWithTimeout('Error resetting password');
    }
  };

  const handleChangePassword = async (userId, currentPwd, newPwd) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL}/api/users/${userId}/change-password`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ currentPassword: currentPwd, newPassword: newPwd })
      });

      if (response.ok) {
        setError('');
        setShowPasswordModal(false);
        setUserToChangePassword(null);
        setNewPassword('');
        setCurrentPassword('');
        setSuccessMessageWithTimeout('Password changed successfully!');
      } else {
        const errorData = await response.json();
        setErrorMessageWithTimeout(errorData.error || 'Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setErrorMessageWithTimeout('Error changing password');
    }
  };

  const openPasswordModal = (userItem) => {
    setUserToChangePassword(userItem);
    setShowPasswordModal(true);
    setNewPassword('');
    setCurrentPassword('');
  };

  const toggleUserStatus = async (userId, isActive) => {
    await handleUpdateUser(userId, { isActive: !isActive });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="user-management">
        <div className="loading">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="user-management-header">
        <h2>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginRight: '8px' }}>
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
          User Management
        </h2>
        <button
          className="btn btn-primary"
          onClick={() => setShowUserForm(true)}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}>
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Add New User
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="success-message">
          {successMessage}
        </div>
      )}

      {/* Users Table */}
      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Full Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Created</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((userItem) => (
              <tr key={userItem.id} className={!userItem.isActive ? 'inactive-user' : ''}>
                <td>{userItem.username}</td>
                <td>{userItem.fullName}</td>
                <td>{userItem.email || 'N/A'}</td>
                <td>
                  <span className={`role-badge ${userItem.role}`}>
                    {userItem.role === 'admin' ? 'Admin' : 'Contract User'}
                  </span>
                </td>
                <td>
                  <span className={`status-badge ${userItem.isActive ? 'active' : 'inactive'}`}>
                    {userItem.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>{formatDate(userItem.createdAt)}</td>
                <td>{userItem.lastLogin ? formatDate(userItem.lastLogin) : 'Never'}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => setEditingUser(userItem)}
                      title="Edit user"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>

                    {user.id !== userItem.id && (
                      <button
                        className={`btn btn-sm ${userItem.isActive ? 'btn-warning' : 'btn-success'}`}
                        onClick={() => toggleUserStatus(userItem.id, userItem.isActive)}
                        title={userItem.isActive ? 'Deactivate user' : 'Activate user'}
                      >
                        {userItem.isActive ? (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                          </svg>
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                            <path d="M7 11V7a5 5 0 0 1 9.9-1"></path>
                          </svg>
                        )}
                      </button>
                    )}

                    <button
                      className="btn btn-sm btn-info"
                      onClick={() => openPasswordModal(userItem)}
                      title={user.id === userItem.id ? "Change my password" : "Reset password"}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"></path>
                      </svg>
                    </button>

                    {user.id !== userItem.id && (
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => {
                          setUserToDelete(userItem);
                          setShowDeleteModal(true);
                        }}
                        title="Delete user"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                      </button>
                    )}

                    {user.id === userItem.id && (
                      <span className="current-user-badge">You</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add User Modal */}
      {showUserForm && (
        <div className="modal-overlay">
          <div className="modal luxury-modal">
            <div className="modal-header">
              <div className="modal-title-with-icon">
                <div className="title-icon-wrapper">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="8.5" cy="7" r="4"></circle>
                    <line x1="20" y1="8" x2="20" y2="14"></line>
                    <line x1="17" y1="11" x2="23" y2="11"></line>
                  </svg>
                </div>
                <h3>Add New User</h3>
              </div>
              <button
                className="modal-close"
                onClick={() => setShowUserForm(false)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <form onSubmit={handleCreateUser} className="luxury-form">
              <div className="form-row">
                <div className="form-group luxury-input">
                  <label>Username *</label>
                  <div className="input-with-icon">
                    <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                    <input
                      type="text"
                      value={newUser.username}
                      onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                      placeholder="Enter username"
                      required
                    />
                  </div>
                </div>
                <div className="form-group luxury-input">
                  <label>Password *</label>
                  <div className="input-with-icon">
                    <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                    <input
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      placeholder="••••••••"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="form-group luxury-input">
                <label>Full Name *</label>
                <div className="input-with-icon">
                  <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                  <input
                    type="text"
                    value={newUser.fullName}
                    onChange={(e) => setNewUser({ ...newUser, fullName: e.target.value })}
                    placeholder="Enter full name"
                    required
                  />
                </div>
              </div>

              <div className="form-group luxury-input">
                <label>Email</label>
                <div className="input-with-icon">
                  <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    placeholder="email@example.com"
                  />
                </div>
              </div>

              <div className="form-group luxury-input">
                <label>Role *</label>
                <div className="input-with-icon">
                  <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    required
                  >
                    <option value="contract_user">Contract User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>

              <div className="modal-actions-luxury">
                <button type="button" className="btn-luxury btn-luxury-secondary" onClick={() => setShowUserForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-luxury btn-luxury-primary">
                  <span>Create User</span>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="modal-overlay">
          <div className="modal luxury-modal">
            <div className="modal-header">
              <div className="modal-title-with-icon">
                <div className="title-icon-wrapper">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </div>
                <h3>{user.id === editingUser.id ? 'Edit My Profile' : `Edit User: ${editingUser.username}`}</h3>
              </div>
              <button
                className="modal-close"
                onClick={() => setEditingUser(null)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const updates = {
                username: formData.get('username'),
                fullName: formData.get('fullName'),
                email: formData.get('email'),
                // Only allow editing role/status if NOT self
                ...(user.id !== editingUser.id && {
                  role: formData.get('role'),
                  isActive: formData.get('isActive') === 'true'
                })
              };
              handleUpdateUser(editingUser.id, updates);
            }} className="luxury-form">
              <div className="form-group luxury-input">
                <label>Username *</label>
                <div className="input-with-icon">
                  <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                  <input
                    type="text"
                    name="username"
                    defaultValue={editingUser.username}
                    required
                  />
                </div>
              </div>
              <div className="form-group luxury-input">
                <label>Full Name *</label>
                <div className="input-with-icon">
                  <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                  <input
                    type="text"
                    name="fullName"
                    defaultValue={editingUser.fullName}
                    required
                  />
                </div>
              </div>
              <div className="form-group luxury-input">
                <label>Email</label>
                <div className="input-with-icon">
                  <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                  <input
                    type="email"
                    name="email"
                    defaultValue={editingUser.email || ''}
                  />
                </div>
              </div>

              {user.id !== editingUser.id && (
                <>
                  <div className="form-group luxury-input">
                    <label>Role *</label>
                    <div className="input-with-icon">
                      <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
                      <select
                        name="role"
                        defaultValue={editingUser.role}
                        required
                      >
                        <option value="contract_user">Contract User</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group luxury-input">
                    <label>Status *</label>
                    <div className="input-with-icon">
                      <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                      <select
                        name="isActive"
                        defaultValue={editingUser.isActive.toString()}
                        required
                      >
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </div>
                  </div>
                </>
              )}

              <div className="modal-actions-luxury">
                <button type="button" className="btn-luxury btn-luxury-secondary" onClick={() => setEditingUser(null)}>
                  Cancel
                </button>
                <button type="submit" className="btn-luxury btn-luxury-primary">
                  <span>Update User</span>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && userToDelete && (
        <div className="modal-overlay">
          <div className="modal luxury-modal modal-small">
            <div className="modal-header">
              <div className="modal-title-with-icon">
                <div className="title-icon-wrapper" style={{ color: 'var(--status-error)', background: 'var(--status-error-bg)' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                </div>
                <h3>Delete User</h3>
              </div>
            </div>
            <div className="luxury-form">
              <p style={{ textAlign: 'center', margin: 'var(--space-2) 0' }}>Are you sure you want to delete user <strong>{userToDelete.username}</strong>?</p>
              <div className="warning" style={{ textAlign: 'center', padding: 'var(--space-3)', background: 'var(--status-error-bg)', borderRadius: 'var(--radius-lg)' }}>
                This action cannot be undone!
              </div>
              <div className="modal-actions-luxury">
                <button
                  className="btn-luxury btn-luxury-secondary"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setUserToDelete(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn-luxury"
                  style={{ background: 'var(--status-error)', color: 'white' }}
                  onClick={handleDeleteUser}
                >
                  Delete User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset/Change Modal */}
      {showPasswordModal && userToChangePassword && (
        <div className="modal-overlay">
          <div className="modal luxury-modal modal-small">
            <div className="modal-header">
              <div className="modal-title-with-icon">
                <div className="title-icon-wrapper">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"></path>
                  </svg>
                </div>
                <h3>{user.id === userToChangePassword.id ? 'Change My Password' : 'Reset User Password'}</h3>
              </div>
              <button
                className="modal-close"
                onClick={() => {
                  setShowPasswordModal(false);
                  setUserToChangePassword(null);
                  setNewPassword('');
                  setCurrentPassword('');
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              if (user.id === userToChangePassword.id) {
                handleChangePassword(userToChangePassword.id, currentPassword, newPassword);
              } else {
                handleResetPassword(userToChangePassword.id, newPassword);
              }
            }} className="luxury-form">

              {/* Show Current Password ONLY for SELF */}
              {user.id === userToChangePassword.id && (
                <div className="form-group luxury-input">
                  <label>Current Password *</label>
                  <div className="input-with-icon">
                    <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                      required
                    />
                  </div>
                </div>
              )}

              <div className="form-group luxury-input">
                <label>New Password {user.id !== userToChangePassword.id ? `for ${userToChangePassword.username}` : ''} *</label>
                <div className="input-with-icon">
                  <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                  />
                </div>
              </div>
              <div className="modal-actions-luxury">
                <button type="button" className="btn-luxury btn-luxury-secondary" onClick={() => {
                  setShowPasswordModal(false);
                  setUserToChangePassword(null);
                  setNewPassword('');
                  setCurrentPassword('');
                }}>
                  Cancel
                </button>
                <button type="submit" className="btn-luxury btn-luxury-primary">
                  {user.id === userToChangePassword.id ? 'Update Password' : 'Reset Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
